import { useCallback, useEffect, useState } from 'react';

export type UseInViewOptions = {
  /**
   * Margin around the viewport, in the IntersectionObserver's `rootMargin`
   * form. `'50% 0px'` reports an element in view half a viewport before it
   * scrolls in, so what it loads is ready when the user gets there.
   * @default '0px'
   */
  rootMargin?: string;
  /**
   * Stop observing once the element has been in view, so `inView` never goes
   * back to false. For content that loads once and then stays.
   * @default true
   */
  once?: boolean;
};

/**
 * Reports whether the element holding `ref` is in the viewport, for content
 * that should not load until the user scrolls to it. An element that is not
 * displayed (inside a closed accordion, say) is never in view, and comes into
 * view on its own once it is displayed. `ref` is a callback ref, so an
 * element that mounts later (behind a loading branch, say) is observed too.
 *
 * @example
 * ```tsx
 * const { ref, inView } = useInView({ rootMargin: '50% 0px' });
 * return <Card ref={ref}>{inView ? <Rows /> : <Spinner />}</Card>;
 * ```
 */
export const useInView = <T extends Element = HTMLDivElement>({
  rootMargin = '0px',
  once = true,
}: UseInViewOptions = {}) => {
  const [element, setElement] = useState<T | null>(null);
  const ref = useCallback((node: T | null) => setElement(node), []);
  // Without an observer (an old browser) nothing would ever come into view,
  // so everything counts as in view from the start.
  const [inView, setInView] = useState(
    () => typeof IntersectionObserver === 'undefined',
  );

  useEffect(() => {
    if (!element || (once && inView)) {
      return;
    }

    const observer = new IntersectionObserver(
      (entries) => {
        // The last entry is the element's current state.
        const isIntersecting = entries.at(-1)?.isIntersecting ?? false;
        if (isIntersecting) {
          setInView(true);
          if (once) {
            observer.disconnect();
          }
        } else if (!once) {
          setInView(false);
        }
      },
      { rootMargin },
    );
    observer.observe(element);

    return () => {
      observer.disconnect();
    };
  }, [element, rootMargin, once, inView]);

  return { ref, inView };
};
